import { getSessionToken } from "./authSession";

const API_BASE_URL = (
  process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8000/api"
).replace(/\/$/, "");
let activeRequestCount = 0;
const requestListeners = new Set();

function notifyRequestListeners() {
  requestListeners.forEach((listener) => listener());
}

function beginRequest() {
  activeRequestCount += 1;
  notifyRequestListeners();
}

function endRequest() {
  activeRequestCount = Math.max(0, activeRequestCount - 1);
  notifyRequestListeners();
}

export function subscribeToRequestActivity(listener) {
  requestListeners.add(listener);
  return () => requestListeners.delete(listener);
}

export function getActiveRequestCount() {
  return activeRequestCount;
}

export class ApiError extends Error {
  constructor(message, { status = 0, code = "NETWORK_ERROR", details = null } = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

function queryString(params = {}) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    (Array.isArray(value) ? value : [value]).forEach((item) => query.append(key, String(item)));
  });
  const result = query.toString();
  return result ? `?${result}` : "";
}

export function xhrRequest(path, {
  method = "GET",
  query,
  body,
  headers = {},
  token,
  timeout = 30000,
  signal,
} = {}) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, `${API_BASE_URL}${path}${queryString(query)}`, true);
    xhr.timeout = timeout;
    xhr.responseType = "json";
    xhr.setRequestHeader("Accept", "application/json");
    if (body !== undefined && !(body instanceof FormData)) xhr.setRequestHeader("Content-Type", "application/json");
    const accessToken = token === undefined ? getSessionToken() : token;
    if (accessToken) xhr.setRequestHeader("Authorization", `Bearer ${accessToken}`);
    Object.entries(headers).forEach(([key, value]) => xhr.setRequestHeader(key, value));

    const abort = () => xhr.abort();
    signal?.addEventListener("abort", abort, { once: true });

    xhr.onload = () => {
      signal?.removeEventListener("abort", abort);
      const payload = xhr.response ?? {};
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(payload);
        return;
      }
      reject(new ApiError(payload.message || "The request could not be completed.", {
        status: xhr.status,
        code: payload.code || "API_ERROR",
        details: payload.details,
      }));
    };
    xhr.onerror = () => reject(new ApiError("Unable to reach Band-Joe. Check your connection and retry."));
    xhr.ontimeout = () => reject(new ApiError("The request timed out.", { code: "TIMEOUT" }));
    xhr.onabort = () => reject(new ApiError("The request was cancelled.", { code: "ABORTED" }));

    let requestEnded = false;
    const finishRequest = () => {
      if (requestEnded) return;
      requestEnded = true;
      endRequest();
    };
    xhr.addEventListener("loadend", finishRequest, { once: true });
    beginRequest();
    try {
      xhr.send(body === undefined || body instanceof FormData ? body : JSON.stringify(body));
    } catch (error) {
      finishRequest();
      reject(error);
    }
  });
}

export const api = {
  get: (path, options) => xhrRequest(path, { ...options, method: "GET" }),
  post: (path, body, options) => xhrRequest(path, { ...options, method: "POST", body }),
  patch: (path, body, options) => xhrRequest(path, { ...options, method: "PATCH", body }),
  delete: (path, options) => xhrRequest(path, { ...options, method: "DELETE" }),
};
